/**
 * ArtifactDocBody — the document panel. Demo page: /file-manager preview pane. Prose kinds render through
 * SafeMarkdown; code and the raw tab show the text verbatim.
 */
import React, { useState } from 'react';
import { Copy } from 'lucide-react';
import { Button, Card } from '../../../components/ui/index.js';
import { SafeMarkdown } from '../../../components/ui/SafeMarkdown.js';

interface ArtifactDocBodyProps {
  content: string;
  kind: string;
  /** Show the source text instead of the rendered document. */
  raw: boolean;
}

/** Strips YAML frontmatter if present, returns the body only. */
function parseBody(content: string): string {
  const match = content.match(/^---\n[\s\S]*?\n---\n([\s\S]*)$/);
  return match ? match[1].trim() : content.trim();
}

export const ArtifactDocBody: React.FC<ArtifactDocBodyProps> = ({ content, kind, raw }) => {
  const [copied, setCopied] = useState(false);
  const body = parseBody(content);
  const verbatim = raw || kind === 'code';

  const copy = () => {
    navigator.clipboard
      .writeText(raw ? content : body)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => setCopied(false));
  };

  return (
    <Card
      actions={
        <Button variant="ghost" size="sm" onClick={copy}>
          <Copy size={14} aria-hidden="true" />
          {copied ? 'Copied' : 'Copy'}
        </Button>
      }
    >
      {verbatim ? (
        <pre className="overflow-x-auto font-mono text-[13px] leading-relaxed whitespace-pre-wrap text-body">
          {raw ? content : body}
        </pre>
      ) : (
        <div className="max-w-3xl text-sm leading-relaxed text-body">
          <SafeMarkdown>{body}</SafeMarkdown>
        </div>
      )}
    </Card>
  );
};
